/**
 * Baseline template planner
 *
 * Pure, store-free resolution of a workspace's Baseline Template policy (see
 * Settings → Track Settings) against a concrete set of items into the lanes
 * ("planned tracks") a cycle should expose, together with each lane's member
 * item IDs.
 *
 * This is the policy engine that connects the admin-defined
 * `BaselineTemplatePolicy` to a cycle's actual items. It is intentionally
 * decoupled from the persisted track/pool model: callers decide how (or
 * whether) to materialize planned tracks.
 *
 * Policy is snapshotted onto a cycle at creation (see
 * `getCycleBaselineTemplatePolicy` / `BASELINE_TEMPLATE_POLICY_METADATA_KEY`)
 * so later admin edits do not retroactively change an existing cycle's tracks.
 */

import type {
  BaselineTemplatePolicy,
  CustomBaselineDef,
} from '../types/prioritization/tenant';
import {
  itemMatchesFrameConditions,
  resolveItemFieldValue,
  type FrameEvaluableItem,
} from './baselineConditionEvaluator';

/** An item the planner can place into lanes. Must carry a stable id. */
export type PlannerItem = FrameEvaluableItem & { id: string };

/** How a planned track was derived. */
export type PlannedTrackMode = 'category-by-altitude' | 'dimensions' | 'conditions';

/** A resolved dimension value for a dimension-mode lane. */
export interface PlannedDimensionValue {
  key: string;
  label: string;
  value: string;
}

/**
 * A single resolved comparability lane: a name, the items that belong to it,
 * and provenance describing how it was derived.
 */
export interface PlannedTrack {
  /** Stable canonical key, unique within a plan. */
  key: string;
  /** Display name for the lane. */
  name: string;
  /** Optional description carried from the custom baseline definition. */
  description?: string;
  /** How this lane was derived. */
  mode: PlannedTrackMode;
  /** Concrete (category, altitude) cell when the lane maps to one. */
  cell?: { itemCategory: string; altitudeLevel: number };
  /** Resolved dimension values for dimension-mode lanes (ordered). */
  dimensionValues?: PlannedDimensionValue[];
  /** Id of the source custom baseline definition (custom mode); undefined otherwise. */
  sourceFrameId?: string;
  /** Item IDs that belong to this lane. */
  itemIds: string[];
}

// ── Cycle policy snapshot helpers ────────────────────────────────────

/** Key under which a cycle's frozen baseline policy is stored in cycle metadata. */
export const BASELINE_TEMPLATE_POLICY_METADATA_KEY = 'baselineTemplatePolicy' as const;

const DEFAULT_POLICY: BaselineTemplatePolicy = { mode: 'category-by-altitude' };

/**
 * Read the policy snapshot frozen onto a cycle at creation. Falls back to the
 * category-by-altitude default for cycles created before snapshots existed.
 */
export function getCycleBaselineTemplatePolicy(
  metadata: Record<string, unknown> | null | undefined,
): BaselineTemplatePolicy {
  const raw = metadata?.[BASELINE_TEMPLATE_POLICY_METADATA_KEY];
  if (raw && typeof raw === 'object' && 'mode' in (raw as Record<string, unknown>)) {
    return raw as BaselineTemplatePolicy;
  }
  return DEFAULT_POLICY;
}

/** Merge a policy snapshot into cycle metadata; preserves unrelated keys. */
export function mergeBaselineTemplatePolicyIntoMetadata(
  existing: Record<string, unknown> | null | undefined,
  policy: BaselineTemplatePolicy,
): Record<string, unknown> {
  return { ...(existing ?? {}), [BASELINE_TEMPLATE_POLICY_METADATA_KEY]: policy };
}

// ── Value helpers ─────────────────────────────────────────────

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function canonicalToken(value: unknown): string {
  return String(value).trim().toLowerCase();
}

/** True when a dimension set is exactly Item Category × Altitude (any order). */
function isCategoryAltitudePair(keys: string[]): boolean {
  return keys.length === 2 && keys.includes('itemCategory') && keys.includes('altitude');
}

function toAltitudeLevel(value: unknown): number {
  const n = Number(value);
  return Number.isNaN(n) ? -1 : n;
}

// ── Plan builders ─────────────────────────────────────────────

/**
 * Auto-generate one lane per unique (Item Category × Altitude) pair present in
 * the items. This is the `category-by-altitude` default behaviour, made
 * explicit and item-driven.
 */
function planCategoryByAltitude(items: PlannerItem[]): PlannedTrack[] {
  const lanes = new Map<string, PlannedTrack>();

  for (const item of items) {
    const category = resolveItemFieldValue(item, 'itemCategory');
    const altitude = resolveItemFieldValue(item, 'altitude');
    if (isEmpty(category) || isEmpty(altitude)) continue;

    const altitudeLevel = toAltitudeLevel(altitude);
    const key = `cat-alt:${canonicalToken(category)}:${altitudeLevel}`;
    const existing = lanes.get(key);
    if (existing) {
      existing.itemIds.push(item.id);
    } else {
      lanes.set(key, {
        key,
        name: `${String(category)} · L${altitudeLevel}`,
        mode: 'category-by-altitude',
        cell: { itemCategory: String(category), altitudeLevel },
        itemIds: [item.id],
      });
    }
  }

  return Array.from(lanes.values());
}

/**
 * Expand a dimension-mode custom baseline into one lane per unique combination
 * of values across its dimensions present in the items. Items missing any
 * dimension value are not placed.
 */
function planDimensionBaseline(frame: CustomBaselineDef, items: PlannerItem[]): PlannedTrack[] {
  const dims = frame.dimensions ?? [];
  if (dims.length === 0) return [];

  const dimKeys = dims.map((d) => d.key);
  const mapsToCell = isCategoryAltitudePair(dimKeys);
  const lanes = new Map<string, PlannedTrack>();

  for (const item of items) {
    const values = dims.map((d) => resolveItemFieldValue(item, d.key));
    if (values.some(isEmpty)) continue;

    const comboKey = `frame:${frame.id}:${values.map(canonicalToken).join('|')}`;
    const existing = lanes.get(comboKey);
    if (existing) {
      existing.itemIds.push(item.id);
      continue;
    }

    const dimensionValues: PlannedDimensionValue[] = dims.map((d, i) => ({
      key: d.key,
      label: d.label,
      value: String(values[i]),
    }));
    const valueLabel = dimensionValues.map((dv) => dv.value).join(' · ');

    const lane: PlannedTrack = {
      key: comboKey,
      name: `${frame.name}: ${valueLabel}`,
      description: frame.description,
      mode: 'dimensions',
      sourceFrameId: frame.id,
      dimensionValues,
      itemIds: [item.id],
    };

    if (mapsToCell) {
      const category = values[dimKeys.indexOf('itemCategory')];
      const altitude = values[dimKeys.indexOf('altitude')];
      lane.cell = { itemCategory: String(category), altitudeLevel: toAltitudeLevel(altitude) };
    }

    lanes.set(comboKey, lane);
  }

  return Array.from(lanes.values());
}

/**
 * A condition-mode custom baseline produces a single lane containing every item
 * that satisfies its inclusion conditions.
 */
function planConditionBaseline(frame: CustomBaselineDef, items: PlannerItem[]): PlannedTrack[] {
  const itemIds = items.filter((item) => itemMatchesFrameConditions(item, frame)).map((i) => i.id);
  return [
    {
      key: `frame:${frame.id}`,
      name: frame.name,
      description: frame.description,
      mode: 'conditions',
      sourceFrameId: frame.id,
      itemIds,
    },
  ];
}

// ── Entry point ───────────────────────────────────────────────

/**
 * Resolve a Baseline Template policy against a set of items into the lanes the
 * cycle should expose, each with its member item IDs.
 *
 * - `category-by-altitude`: one lane per unique (category, altitude) pair.
 * - `custom`: each custom baseline is expanded per its definitionMode
 *   (dimensions → one lane per value combination; conditions → one lane of
 *   matching items). Baselines with no usable definition are skipped.
 *
 * For condition baselines an empty lane is still returned (so an explicitly
 * defined baseline surfaces even with no current members); auto-generated
 * category-by-altitude / dimension expansions only emit lanes that have items.
 */
export function planBaselineTemplate(
  policy: BaselineTemplatePolicy | null | undefined,
  items: PlannerItem[],
): PlannedTrack[] {
  const resolved = policy ?? DEFAULT_POLICY;

  if (resolved.mode !== 'custom') {
    return planCategoryByAltitude(items);
  }

  const frames = resolved.customFrames ?? [];
  const plan: PlannedTrack[] = [];
  for (const frame of frames) {
    const mode = frame.definitionMode ?? 'dimensions';
    if (mode === 'conditions') {
      plan.push(...planConditionBaseline(frame, items));
    } else {
      plan.push(...planDimensionBaseline(frame, items));
    }
  }
  return plan;
}
